import { useState } from "react";
import { Backdrop, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import ToastMessage from "../../utils/mui/righttoptoast";
import api from "../../api/api";
import { externalapi } from "../../api/externalapi";
import { greyuploadicon } from "../../landingscreen/patientcharts/tabs/Document/svgs";
import { searchicongrey } from "../../colorcoding/svg/iconsvg";
import { useRef } from "react";

/* ------------------ CONSTANTS ------------------ */

const CLINICAL_SUB_TYPES = [
  "CLINICALDOCUMENT",
  "ADMISSIONDISCHARGE",
  "CONSULTNOTE",
  "MENTALHEALTH",
  "OPERATIVENOTE",
  "URGENTCARE",
];

const PROXY =
  "/v2/dallas/v1/onboarding-images/proxy?url=";

/* ------------------ COMPONENT ------------------ */

const SendToAthena = ({ open, onClose, selectedFax }) => {
  const practiseid = window.getPracticeId();

  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [openToast, setOpenToast] = useState(false);

  const [patientSearch, setPatientSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
const searchTimeoutRef = useRef(null);

  const [category, setCategory] = useState("IMAGING");
  const [clinicalSubType, setClinicalSubType] =
    useState("CLINICALDOCUMENT");
  const [internalNote, setInternalNote] = useState("");

  if (!open) return null;

  /* ------------------ HELPERS ------------------ */

  const handleToastClose = () => setOpenToast(false);

  const withProxy = (url) =>
    url ? `${PROXY}${encodeURIComponent(url)}` : null;

  const resetForm = () => {
    setPatientSearch("");
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedPatient(null);
    setCategory("IMAGING");
    setClinicalSubType("CLINICALDOCUMENT");
    setInternalNote("");
  };

  /* ------------------ PATIENT SEARCH ------------------ */

const handlePatientSearch = (value) => {
  setPatientSearch(value);
  setSelectedPatient(null);

  // Clear old timer
  if (searchTimeoutRef.current) {
    clearTimeout(searchTimeoutRef.current);
  }

  if (!value.trim()) {
    setSuggestions([]);
    setShowSuggestions(false);
    return;
  }

  // Debounce API call
  searchTimeoutRef.current = setTimeout(async () => {
    try {
      const res = await api.post(
        `/v1/${practiseid}/autocomplete/dssearch`,
        { searchtxt: value }
      );

      if (Array.isArray(res.data)) {
        setSuggestions(res.data.slice(0, 3));
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error("Search error:", error);
    }
  }, 300); // ✅ 300ms debounce
};


  const handleSuggestionSelect = (item) => {
    setPatientSearch(item.patientinfo || "");
    setSelectedPatient(item);
    setShowSuggestions(false);
  };

  /* ------------------ FAX → FILE ------------------ */

  const fetchFaxPdfAsFile = async (faxItem) => {
    const payload = {
      extensionid: faxItem.extensionId,
      attachmentid: faxItem.attachments?.[0]?.id,
      messageid: faxItem.id,
    };

    const res = await externalapi.post(
      "/ringcentral/fax/viewdoc/",
      payload
    );

    if (res.data.result !== "true" || !res.data.documenturl) {
      throw new Error("Fax document not available");
    }

    const pdfRes = await fetch(withProxy(res.data.documenturl));
    const blob = await pdfRes.blob();

    return new File(
      [blob],
      `Fax_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`,
      { type: "application/pdf" }
    );
  };

  /* ------------------ SUBMIT ------------------ */

  const submit = async () => {
    try {
      setLoading(true);

      const file = await fetchFaxPdfAsFile(selectedFax);

      const formData = new FormData();
      formData.append("patientid", selectedPatient.patientid);
      formData.append("departmentid", 1);
      formData.append("internalnote", internalNote || "");
      formData.append("file", file);

      if (category === "CLINICAL") {
        formData.append("documentsubclass", clinicalSubType);

        await api.post(
          `/v1/${practiseid}/fax-documents/add-clinical-document`,
          formData
        );
      } else {
        formData.append("autoclose", true);
        formData.append("documenttypeid", "");
        formData.append("observationtime", "");
        formData.append("observationdate", "");
        formData.append("attachmenttype", "");

       await api.post(
        `/v1/${practiseid}/fax-documents/add-imaging-result`,
        formData,
        {
          headers: {
            // ✅ IMPORTANT
            "Content-Type": "multipart/form-data",
          },
        }
      );
    
      }

      setToastMessage("Fax document uploaded successfully");
      setToastSeverity("success");
      setOpenToast(true);

      setTimeout(() => {
        resetForm();
       handleCloseModal();

      }, 1000);
    } catch (error) {
      console.error(error);
      setToastMessage("Upload failed");
      setToastSeverity("error");
      setOpenToast(true);
    } finally {
      setLoading(false);
    }
  };
const handleCloseModal = () => {
  setOpenToast(false);
  setToastMessage("");
  setToastSeverity("success");
  resetForm();
  onClose();
};

  const isUploadDisabled =
    !selectedPatient || !selectedFax || loading;

  /* ------------------ UI ------------------ */

  return (
    <>
      <Backdrop open={loading} sx={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="fixed inset-0 z-50 bg-black/40">
        <div className="flex min-h-screen items-center justify-center">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-lg">

            {/* Header */}
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div className="flex items-center gap-2 text-lg font-semibold">
                {greyuploadicon}
                Send to Athena
              </div>
              <button  onClick={handleCloseModal}>✕</button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4 text-left">

              {/* Patient Search */}
              <div>
                <label className="text-sm font-semibold">
                  Patient
                </label>
                <div className="relative mt-1">
                  <div className="flex items-center gap-2 rounded-md border bg-[#F9FAFB] px-3 py-2">
                    {searchicongrey}
                    <input
                      type="text"
                      value={patientSearch}
                      onChange={(e) =>
                        handlePatientSearch(e.target.value)
                      }
                      placeholder="Search patient"
                      className="flex-1 bg-transparent text-sm outline-none"
                    />
                  </div>

                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow">
                      {suggestions.map((item, i) => (
                        <div
                          key={i}
                          onClick={() =>
                            handleSuggestionSelect(item)
                          }
                          className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
                        >
                          {item.patientinfo}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Category */}
           <div>
  <label className="text-sm font-semibold">Category</label>

  <div className="mt-2 flex gap-2">
    {["IMAGING", "CLINICAL"].map((t) => (
      <button
        key={t}
        onClick={() => setCategory(t)}
        className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
          category === t
            ? "border-gray-800 bg-gray-100 text-gray-800"
            : "border-gray-300 text-gray-600 hover:bg-gray-50"
        }`}
      >
        {t}
      </button>
    ))}
  </div>
</div>


              {/* Clinical subtype */}
              {category === "CLINICAL" && (
                <div>
                  <label className="text-sm font-semibold">
                    Clinical Document Type
                  </label>
                  <select
                    value={clinicalSubType}
                    onChange={(e) =>
                      setClinicalSubType(e.target.value)
                    }
                    className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                  >
                    {CLINICAL_SUB_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.replace(/([A-Z])/g, " $1")}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Internal Note */}
              <div>
                <label className="text-sm font-semibold">
                  Internal Note
                </label>
                <textarea
                  rows={3}
                  value={internalNote}
                  onChange={(e) =>
                    setInternalNote(e.target.value)
                  }
                  placeholder="Add internal note (optional)"
                  className="mt-1 w-full rounded-md border px-3 py-2 text-sm resize-none"
                />
              </div>

            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
           onClick={handleCloseModal}
                className="rounded-md border px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                disabled={isUploadDisabled}
                onClick={submit}
                className={`rounded-md px-4 py-2 text-sm text-white ${
                  isUploadDisabled
                    ? "bg-gray-300"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Upload
              </button>
            </div>

          </div>
        </div>
      </div>

      <ToastMessage
        message={toastMessage}
        severity={toastSeverity}
        open={openToast}
        handleClose={handleToastClose}
      />
    </>
  );
};

export default SendToAthena;
